import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Skeleton,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
//Need to add this icon to aulaui-core and import from their
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getHomeViewRole } from '@app/selectors/home';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { Lti1p3Launch } from '@app/components/presentational/ClassRoom/Assignments/Lti1p3Launch/Lti1p3Launch';
import { getStarredSpaceColors } from '../../colors';
import { WidgetCard, WidgetErrorState } from '../common';
import ResponsiveModalPageSwitcher from '../../../common/ResponsiveModalPageSwitcher';
import UpcomingAssignmentRow from './UpcomingAssignmentRow';
import UpcomingAssignmentsModal from './UpcomingAssignmentsModal';
import UpcomingAssignmentsPage from './UpcomingAssignmentsPage';
import UpcomingAssignmentsInfoModal from './UpcomingAssignmentsInfoModal';
import useHomeUpcomingAssignments from './useHomeUpcomingAssignments';
import useOpenUpcomingAssignment from './useOpenUpcomingAssignment';
import { getAssignmentTermLabel } from './getAssignmentTermLabel';

const WIDGET_LIMIT = 3;
const WIDGET_KEY = 'home-upcoming-assignments-widget';

const STRINGS = {
  title: 'Upcoming assignments',
  emptyTitle: 'No upcoming assignments',
  emptySubtitle: "New assignments will appear here when they're posted.",
  viewAll: 'View all upcoming assignments',
  hide: 'Hide',
  about: 'About upcoming assignments',
};

const AssignmentRowSkeleton = () => {
  const { palette } = useTheme();

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={2}
      sx={{
        borderBottom: `1px solid ${palette.divider}`,
        mb: '12px',
      }}
    >
      <Skeleton kind="circular" width={40} height={40} />
      <Box flex={1}>
        <Box display="flex" justifyContent="space-between" gap={1}>
          <Skeleton kind="text" width="65%" height={22} />
          <Skeleton kind="circular" width={8} height={8} />
        </Box>
        <Skeleton kind="text" width="45%" height={18} sx={{ mt: 0.5 }} />
        <Skeleton kind="text" width="80%" height={18} />
        <Skeleton kind="text" width="55%" height={18} />
      </Box>
    </Box>
  );
};

const UpcomingAssignmentsWidget = () => {
  const { palette } = useTheme();
  const dispatch = useDispatch<any>();
  const viewRole = useSelector(getHomeViewRole);
  const classRooms = useSelector((state: any) => state.data?.classRooms || {});
  const { allItems, errorType, isLoading, retry } =
    useHomeUpcomingAssignments();
  const { openAssignment, ltiLaunch, clearLtiLaunch, handleLtiLaunchError } =
    useOpenUpcomingAssignment();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const colors = getStarredSpaceColors(palette);

  const handleOpenAssignment = useCallback(
    async (assignment: Parameters<typeof openAssignment>[0]) => {
      setIsModalOpen(false);
      await openAssignment(assignment);
    },
    [openAssignment]
  );

  const getTermLabel = useCallback(
    (spaceId: string) => getAssignmentTermLabel(classRooms[spaceId]),
    [classRooms]
  );

  const filteredItems = useMemo(
    () => allItems.filter((assignment) => assignment.role === viewRole),
    [allItems, viewRole]
  );

  const widgetItems = useMemo(
    () => filteredItems.slice(0, WIDGET_LIMIT),
    [filteredItems]
  );

  const handleHideWidget = () => {
    dispatch(toggleWidgetPreference(WIDGET_KEY, true));
  };

  const menuActions = [{ label: STRINGS.hide, onClick: handleHideWidget }];

  const titleAdornment = (
    <IconButton
      aria-label={STRINGS.about}
      size="small"
      onClick={() => setIsInfoModalOpen(true)}
      sx={{
        width: 24,
        height: 24,
        color: colors.widgetMenuIcon,
        p: 0,
      }}
    >
      <Icons.InfoOutlined sx={{ fontSize: 20 }} />
    </IconButton>
  );

  const footer =
    widgetItems.length > 0 && !errorType ? (
      <Button
        fullWidth
        kind="secondary"
        endIcon={<ArrowForwardIcon />}
        onClick={() => setIsModalOpen(true)}
        disabled={isLoading}
        sx={{
          width: '100%',
          color: colors.starIcon,
          borderRadius: '4px',
        }}
      >
        {STRINGS.viewAll}
      </Button>
    ) : null;

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <>
          <AssignmentRowSkeleton />
          <AssignmentRowSkeleton />
          <AssignmentRowSkeleton />
          <AssignmentRowSkeleton />
        </>
      );
    }
    if (errorType) {
      return (
        <WidgetErrorState
          type={errorType}
          onRetry={retry}
          isRetrying={isLoading}
        />
      );
    }

    if (!widgetItems.length) {
      return (
        <Box
          sx={{
            flex: 1,
            alignSelf: 'stretch',
            display: 'flex',
            width: '100%',
            minHeight: '100%',
            py: 1.5,
          }}
        >
          <Box
            sx={{
              flex: 1,
              width: '100%',
              minHeight: '100%',
              bgcolor: palette.action.hover,
              borderRadius: '12px',
              px: { xs: 2, sm: 3 },
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.primary"
            >
              {STRINGS.emptyTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              {STRINGS.emptySubtitle}
            </Typography>
          </Box>
        </Box>
      );
    }

    return widgetItems.map((assignment, index) => (
      <UpcomingAssignmentRow
        key={assignment.id}
        assignment={assignment}
        viewRole={viewRole}
        termLabel={getTermLabel(assignment.spaceId)}
        showDivider={index < widgetItems.length - 1}
        onOpen={handleOpenAssignment}
      />
    ));
  }, [
    errorType,
    getTermLabel,
    handleOpenAssignment,
    isLoading,
    palette,
    retry,
    viewRole,
    widgetItems,
  ]);

  return (
    <>
      <WidgetCard
        title={STRINGS.title}
        titleAdornment={titleAdornment}
        menuActions={menuActions}
        footer={footer}
        contentGap={0}
        aria-label="Upcoming assignments widget"
      >
        {content}
      </WidgetCard>

      <ResponsiveModalPageSwitcher
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        modalComponent={
          <UpcomingAssignmentsModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            assignments={filteredItems}
            viewRole={viewRole}
            getTermLabel={getTermLabel}
            onOpenAssignment={handleOpenAssignment}
          />
        }
        pageComponent={
          <UpcomingAssignmentsPage
            onClose={() => setIsModalOpen(false)}
            assignments={filteredItems}
            viewRole={viewRole}
            getTermLabel={getTermLabel}
            onOpenAssignment={handleOpenAssignment}
          />
        }
      />

      <UpcomingAssignmentsInfoModal
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />

      {ltiLaunch && (
        <Lti1p3Launch
          providerId={ltiLaunch.providerId}
          resourceLinkId={ltiLaunch.resourceLinkId}
          resourceLinkTitle={ltiLaunch.title}
          assignmentId={ltiLaunch.assignmentId}
          onSuccessLaunch={clearLtiLaunch}
          onErrorLaunch={handleLtiLaunchError}
        />
      )}
    </>
  );
};

export default UpcomingAssignmentsWidget;
